import { childrenOfType } from 'airbnb-prop-types';
import cn from 'classnames';
// import { defer } from 'lodash';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React from 'react';

import NavScrollContext from './navigation-context';
import baseStyles from './PageNavigation.css';
import PageSection from './PageSection';

import MobileNavigation from '../MobileNavigation/MobileNavigation';

import urlCheck from '../../utils/urlCheck';
import { isMobileWidth } from '../../theme/layoutUtilities';
import { genKey } from '../../utils/addKey';
import { makeAnchor } from '../../utils/navigation';
import {
    registerScrollHandler,
    unregisterScrollHandler
} from '../../utils/scroll-handler';

const MOBILE_HEADER_OFFSET = 250;
const DESKTOP_HEADER_OFFSET = 100;

// @TODO - prevent changeHash from being called on a browser back event.
// (i.e - fund screener -> fundbook then click back to return to fund screener...)
const changeHash = debounce(
    (hash) =>
        hash &&
        window.history.replaceState(
            null,
            null,
            `${window.location.pathname}${window.location.search}#${hash}`
        ),
    500,
    { leading: true }
);

/**
 Generic Page Navigation component
 supports both tabbed and scroll behavior
 */
class PageNavigation extends React.Component {
    static propTypes = {
        /**
          child array of page sections
        */
        children: childrenOfType(PageSection),
        /**
          Whether or not navigation is tabbed
          or scroll behavior
        */
        tabbed: PropTypes.bool,
        /**
          id with which to find the element with...
          @note: this is a fix to an annoying
          bug where refs are lost...
        */
        targetId: PropTypes.string,
        /**
          Styles which to extend the base styles with
        */
        styles: PropTypes.string
    };

    static defaultProps = {
        children: [],
        tabbed: false,
        styles: '',
        // @note: this is a fix
        // for an unknown bug where refs are lost
        targetId: `pn-${genKey()}`
    };

    constructor(props) {
        super(props);

        this.idList = this.genIdList();

        const idMap = this.idList.reduce(
            (acc, key, i) => ({ ...acc, [key]: i }),
            {}
        );

        const activeTab =
            idMap[window.location.hash && window.location.hash.slice(1)] || 0;

        this.state = {
            activeTab
        };
    }

    componentDidMount() {
        registerScrollHandler(this.onScroll);
        this.initialScroll();
    }

    componentDidUpdate() {
        this.idList = this.genIdList();
    }

    componentWillUnmount() {
        unregisterScrollHandler(this.onScroll);
    }

    onScroll = () => {
        const { sections } = this;
        const { closestToTop } = sections.reduce((acc, current) => {
            // of the sections which is closest
            // to the top of the viewport
            // need a strategy for resource section
            // where it maybe active but still not
            // closest to top...
            // perhaps we also check if entire section is
            // visible and if so that's likely the active section.
            const scrollDifference = Math.abs(
                window.scrollY - current.offsetTop
            );
            if (!acc.closestToTop || scrollDifference < acc.closestToTopY)
                return {
                    closestToTop: current,
                    closestToTopY: scrollDifference
                };

            return acc;
        }, {});

        const nextActive = sections.findIndex(
            (current) => closestToTop === current
        );

        this.updateActiveTab(nextActive);
    };

    get sections() {
        const sectionContainer = document.getElementById(this.props.targetId);
        return sectionContainer ? Array.from(sectionContainer.children) : [];
    }

    // this function is invoked when a link is clicked

    scrollToSection = (sectionIndex) => {
        // this only should be for scrolling nav not tabbed
        // honestly should probs just be a separate component for tabbed
        const current = this.sections[sectionIndex];
        if (!current) return;
        const top =
            current.offsetTop -
            (isMobileWidth() ? MOBILE_HEADER_OFFSET : DESKTOP_HEADER_OFFSET);

        //
        // switching out scroll options
        // which do not seem to work for ie11
        // without a polyfill
        //
        // window.scrollTo({
        //    left: 0,
        //    top,
        //    behavior: 'smooth'
        // });

        window.scrollTo(0, top);
    };

    updateActiveTab = (activeTab) => {
        if (this.state.activeTab !== activeTab) {
            changeHash(this.idList[activeTab]);
            this.setState({ activeTab });
        }
    };

    initialScroll = () => {
        // scroll to the proper location eh...
        if (this.state.activeTab) {
            // eslint-disable-next-line no-self-assign
            window.location.href = window.location.href;
        }
    };

    linkClick = (e, i) => {
        e.preventDefault();
        if (this.state.activeTab !== i) this.scrollToSection(i);
    };

    genIdList() {
        return React.Children.toArray(this.props.children).map((c) =>
            makeAnchor(c.props.title)
        );
    }

    render() {
        const { tabbed, styles, targetId, children } = this.props;

        const childrenArray = React.Children.toArray(children);

        return (
            <NavScrollContext.Provider value={this.scrollToSection}>
                <div className={cn('page-navigation', { tabbed })}>
                    <MobileNavigation
                        activeTab={this.state.activeTab}
                        onClick={(e, i) => this.linkClick(e, i)}
                        options={childrenArray.map(
                            ({ props: { title, className } }) => ({
                                title,
                                className
                            })
                        )}
                    />
                    <nav>
                        <div className="nav-wrapper">
                            <ul className="desktop">
                                {childrenArray.map(
                                    (
                                        {
                                            props: {
                                                title,
                                                className,
                                                subSections = [],
                                                hideTabNavigation = urlCheck(
                                                    'screenshot'
                                                )
                                            }
                                        },
                                        i
                                    ) =>
                                        !hideTabNavigation && (
                                            <li
                                                key={`nav-link-${title}`}
                                                className={cn(
                                                    {
                                                        active:
                                                            this.state
                                                                .activeTab ===
                                                                i ||
                                                            subSections.filter(
                                                                (ele) =>
                                                                    makeAnchor(
                                                                        ele
                                                                    ) ===
                                                                    this.idList[
                                                                        this
                                                                            .state
                                                                            .activeTab
                                                                    ]
                                                            ).length > 0
                                                    },
                                                    className
                                                )}
                                            >
                                                <a
                                                    onClick={(e) =>
                                                        this.linkClick(e, i)
                                                    }
                                                    href={`#${makeAnchor(
                                                        title
                                                    )}`}
                                                    id={`${makeAnchor(
                                                        title
                                                    )}-link`}
                                                    data-metrics-type="TAB"
                                                >
                                                    {title}
                                                </a>
                                            </li>
                                        )
                                )}
                            </ul>
                        </div>
                    </nav>
                    <div className="sections" id={targetId}>
                        {childrenArray.map((c, i) => (
                            <section
                                className={cn({
                                    active: this.state.activeTab === i
                                })}
                                key={`sect-${c.props.title}`}
                                id={makeAnchor(c.props.title)}
                                data-metrics-location={`SECTION${i + 1}`}
                            >
                                <c.type {...c.props} />
                            </section>
                        ))}
                    </div>
                    <style jsx>{baseStyles}</style>
                    <style jsx>{styles}</style>
                </div>
            </NavScrollContext.Provider>
        );
    }
}

export default PageNavigation;
